import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import {
  CheckCircle2,
  FileSearch,
  History,
  MinusCircle,
  Plus,
  Trash2,
  UploadCloud,
  XCircle,
} from 'lucide-react';
import api from '../lib/axios';
import { dateFmt, euro } from '../lib/format';

// ── API types ────────────────────────────────────────────────────────────────

interface SectionRule {
  starts: string[];
  ends: string[];
  requiredBefore: string[];
}

interface CsvRule {
  separator: string;
  dateColumn: number;
  labelColumn: number;
  debitColumn: number;
  creditColumn: number;
  balanceColumn: number | null;
  dateFormat: string;
  headerLines: number;
}

interface Rules {
  bicPrefixes: string[];
  ibanBankCodes: string[];
  keywords: string[];
  removeSections: SectionRule[];
  ignoreLinePrefixes: string[];
  promptHints: string | null;
  csv: CsvRule | null;
}

interface UsageStats {
  imports: number;
  concordant: number;
  withoutAnchors: number;
  lastUsedAt: string | null;
}

interface Profile {
  id: number;
  code: string;
  name: string;
  enabled: boolean;
  priority: number;
  version: number;
  rules: Rules;
  updatedAt: string;
  updatedBy: string | null;
  stats: UsageStats;
}

interface Listing {
  profiles: Profile[];
  generic: UsageStats;
}

interface VersionRow {
  version: number;
  name: string;
  enabled: boolean;
  priority: number;
  createdAt: string;
  createdBy: string | null;
}

interface Detail {
  profile: Profile;
  versions: VersionRow[];
}

interface ProfileRequest {
  code: string;
  name: string;
  enabled: boolean;
  priority: number;
  rules: Rules;
}

interface Concordance {
  lineCount: number;
  totalCredit: number;
  totalDebit: number;
  statementCredit: number | null;
  statementDebit: number | null;
  statementOpening: number | null;
  closingBalance: number | null;
  computedOpening: number | null;
  creditOk: boolean | null;
  debitOk: boolean | null;
  openingOk: boolean | null;
}

interface TestLine {
  date: string;
  label: string;
  amount: number;
}

interface TestRun {
  concordance: Concordance;
  lines: TestLine[];
}

interface TestResult {
  format: 'PDF' | 'CSV';
  scanned: boolean;
  detection: { code: string; name: string; signal: 'BIC' | 'IBAN' | 'KEYWORD'; value: string; inHeader: boolean } | null;
  appliedCode: string | null;
  appliedName: string | null;
  draftApplied: boolean;
  removedSections: { heading: string; lines: number }[];
  ignoredLines: TestLine[];
  profiled: TestRun;
  generic: TestRun | null;
}

interface TestJob {
  status: 'RUNNING' | 'DONE' | 'ERROR';
  result: TestResult | null;
  error: string | null;
}

// ── Form state (multi-value fields edited as one phrase per line) ────────────

interface SectionForm {
  starts: string;
  ends: string;
  requiredBefore: string;
}

interface CsvForm {
  separator: string;
  dateColumn: string;
  labelColumn: string;
  debitColumn: string;
  creditColumn: string;
  balanceColumn: string;
  dateFormat: string;
  headerLines: string;
}

interface FormState {
  id: number | null;
  code: string;
  name: string;
  enabled: boolean;
  priority: string;
  bic: string;
  iban: string;
  keywords: string;
  sections: SectionForm[];
  ignore: string;
  hints: string;
  csvEnabled: boolean;
  csv: CsvForm;
}

const DEFAULT_CSV: CsvForm = {
  separator: ';',
  dateColumn: '0',
  labelColumn: '1',
  debitColumn: '2',
  creditColumn: '3',
  balanceColumn: '',
  dateFormat: 'dd/MM/yyyy',
  headerLines: '1',
};

const EMPTY_FORM: FormState = {
  id: null,
  code: '',
  name: '',
  enabled: false,
  priority: '100',
  bic: '',
  iban: '',
  keywords: '',
  sections: [],
  ignore: '',
  hints: '',
  csvEnabled: false,
  csv: DEFAULT_CSV,
};

const MAX_HINTS = 2000;
const SEPARATORS = [';', ',', '|', '\\t'];

const toLines = (s: string): string[] =>
  s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);

const toCode = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50);

const apiError = (e: unknown, fallback: string): string =>
  isAxiosError<{ message?: string }>(e) && e.response?.data?.message ? e.response.data.message : fallback;

function fromProfile(p: Profile): FormState {
  const csv = p.rules.csv;
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    enabled: p.enabled,
    priority: String(p.priority),
    bic: p.rules.bicPrefixes.join('\n'),
    iban: p.rules.ibanBankCodes.join('\n'),
    keywords: p.rules.keywords.join('\n'),
    sections: p.rules.removeSections.map((s) => ({
      starts: s.starts.join('\n'),
      ends: s.ends.join('\n'),
      requiredBefore: s.requiredBefore.join('\n'),
    })),
    ignore: p.rules.ignoreLinePrefixes.join('\n'),
    hints: p.rules.promptHints ?? '',
    csvEnabled: csv != null,
    csv: csv
      ? {
          separator: csv.separator,
          dateColumn: String(csv.dateColumn),
          labelColumn: String(csv.labelColumn),
          debitColumn: String(csv.debitColumn),
          creditColumn: String(csv.creditColumn),
          balanceColumn: csv.balanceColumn == null ? '' : String(csv.balanceColumn),
          dateFormat: csv.dateFormat,
          headerLines: String(csv.headerLines),
        }
      : DEFAULT_CSV,
  };
}

function toRequest(f: FormState): ProfileRequest {
  return {
    code: f.code.trim(),
    name: f.name.trim(),
    enabled: f.enabled,
    priority: Number(f.priority) || 0,
    rules: {
      bicPrefixes: toLines(f.bic),
      ibanBankCodes: toLines(f.iban),
      keywords: toLines(f.keywords),
      removeSections: f.sections.map((s) => ({
        starts: toLines(s.starts),
        ends: toLines(s.ends),
        requiredBefore: toLines(s.requiredBefore),
      })),
      ignoreLinePrefixes: toLines(f.ignore),
      promptHints: f.hints.trim() || null,
      csv: f.csvEnabled
        ? {
            separator: f.csv.separator,
            dateColumn: Number(f.csv.dateColumn) || 0,
            labelColumn: Number(f.csv.labelColumn) || 0,
            debitColumn: Number(f.csv.debitColumn) || 0,
            creditColumn: Number(f.csv.creditColumn) || 0,
            balanceColumn: f.csv.balanceColumn.trim() === '' ? null : Number(f.csv.balanceColumn),
            dateFormat: f.csv.dateFormat.trim(),
            headerLines: Number(f.csv.headerLines) || 0,
          }
        : null,
    },
  };
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function BankProfiles() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState | null>(null);
  const [codeTouched, setCodeTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const listing = useQuery({
    queryKey: ['bank-profiles'],
    queryFn: () => api.get<Listing>('/admin/bank-profiles').then((r) => r.data),
  });

  const editingId = form?.id ?? null;
  const detail = useQuery({
    queryKey: ['bank-profile', editingId],
    queryFn: () => api.get<Detail>(`/admin/bank-profiles/${editingId}`).then((r) => r.data),
    enabled: editingId != null,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['bank-profiles'] });
    qc.invalidateQueries({ queryKey: ['bank-profile'] });
  };

  const onSaved = (p: Profile) => {
    refresh();
    setForm(fromProfile(p));
    setError(null);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2500);
  };

  const save = useMutation({
    mutationFn: (f: FormState) =>
      (f.id == null
        ? api.post<Profile>('/admin/bank-profiles', toRequest(f))
        : api.put<Profile>(`/admin/bank-profiles/${f.id}`, toRequest(f))
      ).then((r) => r.data),
    onSuccess: onSaved,
    onError: (e: unknown) => setError(apiError(e, t('bankProfiles.saveError'))),
  });

  const toggle = useMutation({
    mutationFn: (p: Profile) =>
      api
        .post<Profile>(`/admin/bank-profiles/${p.id}/enabled`, null, { params: { enabled: !p.enabled } })
        .then((r) => r.data),
    onSuccess: refresh,
    onError: (e: unknown) => window.alert(apiError(e, t('bankProfiles.saveError'))),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/bank-profiles/${id}`),
    onSuccess: () => {
      refresh();
      setForm(null);
    },
    onError: (e: unknown) => window.alert(apiError(e, t('bankProfiles.saveError'))),
  });

  const restore = useMutation({
    mutationFn: ({ id, version }: { id: number; version: number }) =>
      api.post<Profile>(`/admin/bank-profiles/${id}/rollback/${version}`).then((r) => r.data),
    onSuccess: onSaved,
    onError: (e: unknown) => window.alert(apiError(e, t('bankProfiles.saveError'))),
  });

  const openCreate = () => {
    setError(null);
    setCodeTouched(false);
    setForm({ ...EMPTY_FORM });
  };

  const openEdit = (p: Profile) => {
    setError(null);
    setCodeTouched(true);
    setForm(fromProfile(p));
  };

  const patch = (changes: Partial<FormState>) => setForm((f) => (f ? { ...f, ...changes } : f));

  const profiles = listing.data?.profiles ?? [];
  const detailData = detail.data;

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t('nav.groupPlatform')}</p>
          <h1 className="page-title mt-1">{t('bankProfiles.title')}</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-muted">{t('bankProfiles.intro')}</p>
        </div>
        <button onClick={() => (form ? setForm(null) : openCreate())} className={form ? 'btn btn-secondary' : 'btn btn-primary'}>
          {form ? (
            t('common.close')
          ) : (
            <>
              <Plus size={16} /> {t('bankProfiles.new')}
            </>
          )}
        </button>
      </div>

      {listing.isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}

      {listing.data && (
        <div className="card mb-6 overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-5 py-3">
            <span className="font-medium text-ink">{t('bankProfiles.genericTitle')}</span>
            <span className="text-sm text-ink-soft" title={t('bankProfiles.genericHint')}>
              <StatsText stats={listing.data.generic} />
            </span>
          </div>
          {profiles.length === 0 ? (
            <div className="p-8 text-center text-ink-muted">{t('bankProfiles.empty')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('bankProfiles.colName')}</th>
                    <th>{t('bankProfiles.colStatus')}</th>
                    <th className="text-right">{t('bankProfiles.colPriority')}</th>
                    <th className="text-right">{t('bankProfiles.colVersion')}</th>
                    <th>{t('bankProfiles.colConcordance')}</th>
                    <th className="text-right">{t('field.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {profiles.map((p) => (
                    <tr key={p.id} className={form?.id === p.id ? 'bg-brand-tint/30' : ''}>
                      <td data-label={t('bankProfiles.colName')}>
                        <span className="font-medium text-ink">{p.name}</span>
                        <span className="ml-2 rounded bg-surface-raised px-1.5 py-0.5 text-xs text-ink-soft tnum">
                          {p.code}
                        </span>
                      </td>
                      <td data-label={t('bankProfiles.colStatus')}>
                        <span className={p.enabled ? 'pill pill-posted' : 'pill pill-neutral'}>
                          {p.enabled ? t('bankProfiles.enabled') : t('bankProfiles.disabled')}
                        </span>
                      </td>
                      <td data-label={t('bankProfiles.colPriority')} className="text-right tnum">{p.priority}</td>
                      <td data-label={t('bankProfiles.colVersion')} className="text-right tnum">v{p.version}</td>
                      <td data-label={t('bankProfiles.colConcordance')} className="text-sm text-ink-soft">
                        <StatsText stats={p.stats} />
                      </td>
                      <td data-label={t('field.actions')} className="cell-actions text-right">
                        <div className="flex flex-wrap justify-end gap-2">
                          <button
                            className="btn btn-ghost !px-2 !py-1 text-sm"
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(p)}
                          >
                            {p.enabled ? t('bankProfiles.disable') : t('bankProfiles.enable')}
                          </button>
                          <button className="btn btn-secondary !px-3 !py-1 text-sm" onClick={() => openEdit(p)}>
                            {t('common.edit')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {form && (
        <div className="reveal card mb-6 p-5 sm:p-6">
          <h2 className="font-display text-lg font-semibold">
            {form.id == null ? t('bankProfiles.newTitle') : t('bankProfiles.editTitle', { name: form.name })}
          </h2>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-12">
            <div className="md:col-span-5">
              <label className="field-label">{t('bankProfiles.name')}</label>
              <input
                className="input"
                value={form.name}
                placeholder="Crédit Agricole"
                onChange={(e) =>
                  patch({ name: e.target.value, code: codeTouched ? form.code : toCode(e.target.value) })
                }
              />
            </div>
            <div className="md:col-span-4">
              <label className="field-label">{t('bankProfiles.code')}</label>
              <input
                className="input tnum"
                value={form.code}
                spellCheck={false}
                disabled={form.id != null}
                onChange={(e) => {
                  setCodeTouched(true);
                  patch({ code: e.target.value.toUpperCase() });
                }}
              />
              <p className="mt-1 text-xs text-ink-muted">{t('bankProfiles.codeHint')}</p>
            </div>
            <div className="md:col-span-3">
              <label className="field-label">{t('bankProfiles.priority')}</label>
              <input
                type="number"
                min={0}
                max={1000}
                className="input tnum"
                value={form.priority}
                onChange={(e) => patch({ priority: e.target.value })}
              />
              <p className="mt-1 text-xs text-ink-muted">{t('bankProfiles.priorityHint')}</p>
            </div>
            <label className="flex items-center gap-2 text-sm text-ink md:col-span-12">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => patch({ enabled: e.target.checked })}
                className="h-4 w-4 accent-[var(--brand)]"
              />
              {t('bankProfiles.enabledLabel')}
            </label>
          </div>

          <FormSection title={t('bankProfiles.detectionTitle')} hint={t('bankProfiles.detectionHint')}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <PhraseArea label={t('bankProfiles.bicPrefixes')} value={form.bic} placeholder="CCBP" onChange={(v) => patch({ bic: v })} />
              <PhraseArea label={t('bankProfiles.ibanBankCodes')} value={form.iban} placeholder="10207" onChange={(v) => patch({ iban: v })} />
              <PhraseArea label={t('bankProfiles.keywords')} value={form.keywords} placeholder="BANQUE POPULAIRE" onChange={(v) => patch({ keywords: v })} />
            </div>
          </FormSection>

          <FormSection title={t('bankProfiles.sectionsTitle')} hint={t('bankProfiles.sectionsHint')}>
            <div className="space-y-4">
              {form.sections.map((s, i) => {
                const setSection = (changes: Partial<SectionForm>) =>
                  patch({ sections: form.sections.map((x, j) => (j === i ? { ...x, ...changes } : x)) });
                return (
                  <div key={i} className="rounded-xl border border-line p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <span className="text-sm font-medium text-ink">{t('bankProfiles.sectionN', { n: i + 1 })}</span>
                      <button
                        type="button"
                        className="btn btn-ghost !px-2 !py-1 text-sm text-expense"
                        onClick={() => patch({ sections: form.sections.filter((_, j) => j !== i) })}
                      >
                        <Trash2 size={14} /> {t('bankProfiles.removeSection')}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <PhraseArea
                        label={t('bankProfiles.sectionRequired')}
                        value={s.requiredBefore}
                        placeholder="TOTAL DES MOUVEMENTS CREDITEURS"
                        onChange={(v) => setSection({ requiredBefore: v })}
                      />
                      <PhraseArea
                        label={t('bankProfiles.sectionStarts')}
                        value={s.starts}
                        placeholder="DETAIL DE VOS MOUVEMENTS SEPA"
                        onChange={(v) => setSection({ starts: v })}
                      />
                      <PhraseArea
                        label={t('bankProfiles.sectionEnds')}
                        hint={t('bankProfiles.sectionEndsHint')}
                        value={s.ends}
                        placeholder="LIBELLE / REFERENCE"
                        onChange={(v) => setSection({ ends: v })}
                      />
                    </div>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => patch({ sections: [...form.sections, { starts: '', ends: '', requiredBefore: '' }] })}
              >
                <Plus size={16} /> {t('bankProfiles.addSection')}
              </button>
            </div>
          </FormSection>

          <FormSection title={t('bankProfiles.ignoreTitle')} hint={t('bankProfiles.ignoreHint')}>
            <PhraseArea value={form.ignore} placeholder={'REPORT\nA REPORTER'} onChange={(v) => patch({ ignore: v })} />
          </FormSection>

          <FormSection title={t('bankProfiles.hintsTitle')} hint={t('bankProfiles.hintsHint')}>
            <textarea
              className="input min-h-[7rem] text-sm"
              value={form.hints}
              maxLength={MAX_HINTS}
              onChange={(e) => patch({ hints: e.target.value })}
            />
            <p className="mt-1 text-right text-xs text-ink-muted tnum">
              {form.hints.length} / {MAX_HINTS}
            </p>
          </FormSection>

          <FormSection title={t('bankProfiles.csvTitle')}>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={form.csvEnabled}
                onChange={(e) => patch({ csvEnabled: e.target.checked })}
                className="h-4 w-4 accent-[var(--brand)]"
              />
              {t('bankProfiles.csvEnabled')}
            </label>
            {form.csvEnabled && (
              <>
                <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                  <div>
                    <label className="field-label">{t('bankProfiles.csvSeparator')}</label>
                    <select
                      className="select"
                      value={form.csv.separator}
                      onChange={(e) => patch({ csv: { ...form.csv, separator: e.target.value } })}
                    >
                      {SEPARATORS.map((s) => (
                        <option key={s} value={s}>
                          {s === '\\t' ? 'Tab' : s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <CsvField label={t('bankProfiles.csvDateFormat')} value={form.csv.dateFormat} onChange={(v) => patch({ csv: { ...form.csv, dateFormat: v } })} />
                  <CsvField label={t('bankProfiles.csvHeaderLines')} numeric value={form.csv.headerLines} onChange={(v) => patch({ csv: { ...form.csv, headerLines: v } })} />
                  <CsvField label={t('bankProfiles.csvDateColumn')} numeric value={form.csv.dateColumn} onChange={(v) => patch({ csv: { ...form.csv, dateColumn: v } })} />
                  <CsvField label={t('bankProfiles.csvLabelColumn')} numeric value={form.csv.labelColumn} onChange={(v) => patch({ csv: { ...form.csv, labelColumn: v } })} />
                  <CsvField label={t('bankProfiles.csvDebitColumn')} numeric value={form.csv.debitColumn} onChange={(v) => patch({ csv: { ...form.csv, debitColumn: v } })} />
                  <CsvField label={t('bankProfiles.csvCreditColumn')} numeric value={form.csv.creditColumn} onChange={(v) => patch({ csv: { ...form.csv, creditColumn: v } })} />
                  <CsvField label={t('bankProfiles.csvBalanceColumn')} numeric value={form.csv.balanceColumn} onChange={(v) => patch({ csv: { ...form.csv, balanceColumn: v } })} />
                </div>
                <p className="mt-1 text-xs text-ink-muted">{t('bankProfiles.csvColumnsHint')}</p>
              </>
            )}
          </FormSection>

          {error && (
            <div className="mt-5 rounded-xl border border-expense/40 bg-expense-tint/40 p-3 text-sm text-expense">{error}</div>
          )}

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate(form)}>
              {save.isPending ? t('common.saving') : t('common.save')}
            </button>
            <button className="btn btn-ghost" onClick={() => setForm(null)}>
              {t('common.cancel')}
            </button>
            {saved && <span className="text-sm font-medium text-income">{t('settings.saved')}</span>}
            {form.id != null && (
              <button
                className="btn btn-danger ml-auto"
                disabled={remove.isPending}
                onClick={() => {
                  if (form.id != null && window.confirm(t('bankProfiles.deleteConfirm', { name: form.name }))) {
                    remove.mutate(form.id);
                  }
                }}
              >
                <Trash2 size={16} /> {t('common.delete')}
              </button>
            )}
          </div>

          {detailData && detailData.versions.length > 0 && (
            <div className="mt-6 border-t border-line pt-5">
              <div className="field-label flex items-center gap-2">
                <History size={16} className="text-brand" /> {t('bankProfiles.versionsTitle')}
              </div>
              <ul className="mt-2 divide-y divide-line">
                {detailData.versions.map((v) => (
                  <li key={v.version} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                    <span className="tnum font-medium text-ink">v{v.version}</span>
                    <span className="text-ink-soft">{dateFmt(v.createdAt)}</span>
                    <span className="text-ink-muted">{v.createdBy ?? '-'}</span>
                    <span className={v.enabled ? 'pill pill-posted' : 'pill pill-neutral'}>
                      {v.enabled ? t('bankProfiles.enabled') : t('bankProfiles.disabled')}
                    </span>
                    {v.version === detailData.profile.version ? (
                      <span className="ml-auto text-xs text-ink-muted">{t('bankProfiles.current')}</span>
                    ) : (
                      <button
                        className="btn btn-ghost ml-auto !px-2 !py-1 text-sm"
                        disabled={restore.isPending}
                        onClick={() => {
                          if (window.confirm(t('bankProfiles.restoreConfirm', { version: v.version }))) {
                            restore.mutate({ id: detailData.profile.id, version: v.version });
                          }
                        }}
                      >
                        {t('bankProfiles.restore')}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <TestBench draft={form ? toRequest(form) : null} />
    </div>
  );
}

// ── Building blocks ──────────────────────────────────────────────────────────

function StatsText({ stats }: { stats: UsageStats }) {
  const { t } = useTranslation();
  if (stats.imports === 0) return <>{t('bankProfiles.noImports')}</>;
  const checked = stats.imports - stats.withoutAnchors;
  return (
    <>
      <span className="tnum">{t('bankProfiles.concordance', { ok: stats.concordant, total: checked })}</span>
      {stats.withoutAnchors > 0 && (
        <span className="ml-2 text-xs text-ink-muted">{t('bankProfiles.noAnchors', { count: stats.withoutAnchors })}</span>
      )}
      {stats.lastUsedAt && <span className="ml-2 text-xs text-ink-muted">· {dateFmt(stats.lastUsedAt)}</span>}
    </>
  );
}

function FormSection({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="mt-6 border-t border-line pt-5">
      <h3 className="font-display text-base font-semibold text-ink">{title}</h3>
      {hint && <p className="mt-1 max-w-3xl text-sm text-ink-muted">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  );
}

function PhraseArea({
  label,
  hint,
  value,
  placeholder,
  onChange,
}: {
  label?: string;
  hint?: string;
  value: string;
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div>
      {label && <label className="field-label">{label}</label>}
      <textarea
        className="input min-h-[5.5rem] text-sm"
        value={value}
        placeholder={placeholder}
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="mt-1 text-xs text-ink-muted">{hint ?? t('bankProfiles.linesHint')}</p>
    </div>
  );
}

function CsvField({
  label,
  value,
  numeric,
  onChange,
}: {
  label: string;
  value: string;
  numeric?: boolean;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        className="input tnum"
        type={numeric ? 'number' : 'text'}
        min={numeric ? 0 : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function CheckIcon({ ok }: { ok: boolean | null }) {
  if (ok == null) return <MinusCircle size={16} className="text-ink-muted" aria-hidden />;
  return ok ? (
    <CheckCircle2 size={16} className="text-income" aria-hidden />
  ) : (
    <XCircle size={16} className="text-expense" aria-hidden />
  );
}

function TestBench({ draft }: { draft: ProfileRequest | null }) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  const [forceDraft, setForceDraft] = useState(false);
  const [compareGeneric, setCompareGeneric] = useState(true);
  const [testId, setTestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showLines, setShowLines] = useState(false);

  const start = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      if (file) fd.append('file', file);
      if (draft) fd.append('draft', JSON.stringify(draft));
      fd.append('forceDraft', String(forceDraft && !!draft));
      fd.append('compareGeneric', String(compareGeneric));
      return api
        .post<{ testId: string }>('/admin/bank-profiles/test', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        .then((r) => r.data);
    },
    onMutate: () => {
      setError(null);
      setTestId(null);
    },
    onSuccess: (r) => setTestId(r.testId),
    onError: (e: unknown) => setError(apiError(e, t('bankProfiles.testError'))),
  });

  const job = useQuery({
    queryKey: ['bank-profile-test', testId],
    queryFn: () => api.get<TestJob>(`/admin/bank-profiles/test/${testId}`).then((r) => r.data),
    enabled: testId != null,
    refetchInterval: (q) => (q.state.data == null || q.state.data.status === 'RUNNING' ? 2000 : false),
  });

  const running = start.isPending || job.data?.status === 'RUNNING' || (testId != null && !job.data);
  const result = job.data?.status === 'DONE' ? job.data.result : null;
  const jobError = job.data?.status === 'ERROR' ? job.data.error : null;

  return (
    <div className="card p-5 sm:p-6">
      <div className="field-label flex items-center gap-2">
        <FileSearch size={16} className="text-brand" /> {t('bankProfiles.testTitle')}
      </div>
      <p className="mt-1 max-w-3xl text-sm text-ink-muted">{t('bankProfiles.testIntro')}</p>
      {draft && <p className="mt-1 text-sm text-ink-soft">{t('bankProfiles.testDraftHint')}</p>}

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-12 sm:items-end">
        <div className="sm:col-span-6">
          <label className="field-label">{t('bankProfiles.testFile')}</label>
          <label className="input flex cursor-pointer items-center gap-2 overflow-hidden hover:border-brand/50">
            <span className="grid h-6 w-6 shrink-0 place-items-center rounded-md bg-brand-tint text-brand">
              <UploadCloud size={14} />
            </span>
            <span className={`truncate ${file ? 'text-ink' : 'text-ink-muted'}`}>
              {file ? file.name : t('imports.chooseFile')}
            </span>
            <input
              type="file"
              accept=".csv,.pdf"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </label>
        </div>
        <div className="space-y-2 sm:col-span-4">
          {draft && (
            <label className="flex items-center gap-2 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={forceDraft}
                onChange={(e) => setForceDraft(e.target.checked)}
                className="h-4 w-4 accent-[var(--brand)]"
              />
              {t('bankProfiles.testForce')}
            </label>
          )}
          <label className="flex items-center gap-2 text-sm text-ink-soft">
            <input
              type="checkbox"
              checked={compareGeneric}
              onChange={(e) => setCompareGeneric(e.target.checked)}
              className="h-4 w-4 accent-[var(--brand)]"
            />
            {t('bankProfiles.testCompare')}
          </label>
        </div>
        <div className="sm:col-span-2">
          <button className="btn btn-primary w-full" disabled={!file || running} onClick={() => start.mutate()}>
            {running ? t('bankProfiles.testRunning') : t('bankProfiles.testRun')}
          </button>
        </div>
      </div>

      {(error || jobError) && (
        <div className="mt-4 rounded-xl border border-expense/40 bg-expense-tint/40 p-3 text-sm text-expense">
          {error ?? jobError}
        </div>
      )}

      {result && (
        <div className="reveal mt-6 space-y-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-line p-4">
              <p className="field-label">{t('bankProfiles.detected')}</p>
              {result.detection ? (
                <p className="mt-1 text-sm text-ink">
                  <span className="font-medium">{result.detection.name}</span>{' '}
                  <span className="text-ink-soft">
                    · {t(`bankProfiles.signal.${result.detection.signal}`)}{' '}
                    <span className="tnum">{result.detection.value}</span> ·{' '}
                    {result.detection.inHeader ? t('bankProfiles.inHeader') : t('bankProfiles.outsideHeader')}
                  </span>
                </p>
              ) : (
                <p className="mt-1 text-sm text-ink-soft">{t('bankProfiles.notDetected')}</p>
              )}
              <p className="field-label mt-3">{t('bankProfiles.applied')}</p>
              <p className="mt-1 text-sm text-ink">
                {result.appliedName ?? t('bankProfiles.appliedNone')}
                {result.draftApplied && (
                  <span className="ml-2 pill pill-draft">{t('bankProfiles.draft')}</span>
                )}
              </p>
              {result.scanned && <p className="mt-3 text-sm text-gold">{t('bankProfiles.scanned')}</p>}
            </div>
            <div className="rounded-xl border border-line p-4">
              <p className="field-label">{t('bankProfiles.removedTitle')}</p>
              {result.removedSections.length === 0 ? (
                <p className="mt-1 text-sm text-ink-soft">{t('bankProfiles.removedNone')}</p>
              ) : (
                <ul className="mt-1 space-y-1 text-sm text-ink">
                  {result.removedSections.map((s, i) => (
                    <li key={i}>{t('bankProfiles.removedLine', { heading: s.heading, count: s.lines })}</li>
                  ))}
                </ul>
              )}
              <p className="field-label mt-3">{t('bankProfiles.ignoredTitle')}</p>
              {result.ignoredLines.length === 0 ? (
                <p className="mt-1 text-sm text-ink-soft">{t('bankProfiles.ignoredNone')}</p>
              ) : (
                <ul className="mt-1 space-y-1 text-sm text-ink">
                  {result.ignoredLines.map((l, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span className="truncate">{l.label}</span>
                      <span className="tnum shrink-0">{euro(l.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <ConcordanceTable profiled={result.profiled.concordance} generic={result.generic?.concordance ?? null} hasProfile={result.appliedCode != null} />

          <div>
            <button className="btn btn-ghost !px-2 text-sm" onClick={() => setShowLines((v) => !v)}>
              {showLines ? t('bankProfiles.hideLines') : t('bankProfiles.showLines')} (
              {result.profiled.lines.length})
            </button>
            {showLines && (
              <div className="mt-2 max-h-96 overflow-auto rounded-xl border border-line">
                <table className="table">
                  <tbody>
                    {result.profiled.lines.map((l, i) => (
                      <tr key={i}>
                        <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">{dateFmt(l.date)}</td>
                        <td data-label={t('field.bankLabel')}>{l.label}</td>
                        <td data-label={t('field.amount')} className={`num ${l.amount < 0 ? 'text-expense' : 'text-income'}`}>
                          {euro(l.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ConcordanceTable({
  profiled,
  generic,
  hasProfile,
}: {
  profiled: Concordance;
  generic: Concordance | null;
  hasProfile: boolean;
}) {
  const { t } = useTranslation();
  const rows: { key: string; label: string; printed: number | null; pick: (c: Concordance) => [number | null, boolean | null] }[] = [
    { key: 'credit', label: t('bankProfiles.totalCredit'), printed: profiled.statementCredit, pick: (c) => [c.totalCredit, c.creditOk] },
    { key: 'debit', label: t('bankProfiles.totalDebit'), printed: profiled.statementDebit, pick: (c) => [c.totalDebit, c.debitOk] },
    { key: 'opening', label: t('bankProfiles.opening'), printed: profiled.statementOpening, pick: (c) => [c.computedOpening, c.openingOk] },
  ];
  const cell = (value: number | null, ok: boolean | null) => (
    <span className="inline-flex items-center justify-end gap-2">
      <span className="tnum">{value == null ? '-' : euro(value)}</span>
      <CheckIcon ok={ok} />
    </span>
  );
  return (
    <div className="overflow-x-auto rounded-xl border border-line">
      <table className="table">
        <thead>
          <tr>
            <th>{t('bankProfiles.check')}</th>
            <th className="num">{t('bankProfiles.printed')}</th>
            <th className="num">{hasProfile ? t('bankProfiles.withProfile') : t('bankProfiles.generic')}</th>
            {generic && <th className="num">{t('bankProfiles.generic')}</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const [pv, pok] = r.pick(profiled);
            const g = generic ? r.pick(generic) : null;
            return (
              <tr key={r.key}>
                <td data-label={t('bankProfiles.check')} className="font-medium text-ink">{r.label}</td>
                <td data-label={t('bankProfiles.printed')} className="num text-ink-soft">
                  {r.printed == null ? t('bankProfiles.notPrinted') : euro(r.printed)}
                </td>
                <td data-label={hasProfile ? t('bankProfiles.withProfile') : t('bankProfiles.generic')} className="num">
                  {cell(pv, pok)}
                </td>
                {g && (
                  <td data-label={t('bankProfiles.generic')} className="num">
                    {cell(g[0], g[1])}
                  </td>
                )}
              </tr>
            );
          })}
          <tr>
            <td data-label={t('bankProfiles.check')} className="font-medium text-ink">{t('bankProfiles.lineCount')}</td>
            <td data-label={t('bankProfiles.printed')} className="num text-ink-soft">-</td>
            <td data-label={hasProfile ? t('bankProfiles.withProfile') : t('bankProfiles.generic')} className="num tnum">
              {profiled.lineCount}
            </td>
            {generic && (
              <td data-label={t('bankProfiles.generic')} className="num tnum">
                {generic.lineCount}
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}
